import { useCallback } from 'react';
import { useSubjects as useCoreSubjects } from '@ts-core/modules/subjects/hooks';
import { useMutation } from '@ts-core/lib/apollo';
import cloneDeep from 'lodash/cloneDeep';

import { findSubject } from '@ts-core/modules/subjects/utils';
import { reorder } from '@ts-core/utils/utils';

import { useMutationCompletionHandler } from '@ts-core/lib/apollo';
import { getSubjectsByCourseQuery } from '@ts-core/modules/subjects/graphql/queries/getSubjectsByCourseQuery';

import {
  createSubjectMutation,
  updateSubjectMutation,
  deleteSubjectMutation,
  toggleSubjectMutation,
  reorderSubjectMutation,
} from '../graphql/mutations';
import { subjectsTreeFragment } from '../graphql/fragments';

export default function useSubjects({ courseId, onSubjectCreated, onSubjectUpdated }) {
  const { subjects, loading, error } = useCoreSubjects({
    courseId,
    subjectsTreeFragment,
  });

  const { onError } = useMutationCompletionHandler();

  const updateSubjectsList = (store, { data }) => {
    const updatedSubjects = data?.subjects;

    if (updatedSubjects) {
      store.writeQuery({
        query: getSubjectsByCourseQuery(subjectsTreeFragment),
        variables: { courseId },
        data: { subjects: updatedSubjects },
      });
    }
  };

  const locallyReorderSubjects = useCallback(
    (from, to, parentSubjectId) => {
      const updatedSubjects = cloneDeep(subjects);
      if (parentSubjectId) {
        const parentSubject = findSubject(updatedSubjects, parentSubjectId);

        if (parentSubject && parentSubject.subjects) {
          parentSubject.subjects = reorder(parentSubject.subjects, from, to);
          return updatedSubjects;
        }
      } else {
        return reorder(updatedSubjects, from, to);
      }
      return updatedSubjects;
    },
    [subjects]
  );

  /**
   * Add Subject
   */
  const [createSubject, { loading: creatingSubject }] = useMutation(createSubjectMutation, {
    onError,
    onCompleted: onSubjectCreated,
    update: updateSubjectsList,
  });
  const onCreateSubject = useCallback(
    (input) => {
      createSubject({
        variables: {
          courseId,
          input,
        },
      });
    },
    [createSubject, courseId]
  );

  /**
   * Update Subject
   */
  const [updateSubject, { loading: updatingSubject }] = useMutation(updateSubjectMutation, {
    onError,
    onCompleted: onSubjectUpdated,
  });
  const onUpdateSubject = useCallback(
    (subjectId, subject) => {
      updateSubject({
        variables: {
          courseId,
          subjectId,
          input: subject,
        },
      });
    },
    [updateSubject, courseId]
  );

  /**
   * Delete Subject
   */
  const [deleteSubject] = useMutation(deleteSubjectMutation, {
    onError,
    update: updateSubjectsList,
  });
  const onDeleteSubject = useCallback(
    (subject) => {
      const { _id } = subject;
      deleteSubject({
        variables: {
          courseId,
          subjectId: _id,
        },
      });
    },
    [deleteSubject, courseId]
  );

  /**
   * Toggle Subject
   */
  const [toggleSubject] = useMutation(toggleSubjectMutation, { onError });
  const onToggleSubject = useCallback(
    (subject) => {
      const updatedSubject = { ...subject, enabled: !subject.enabled };
      toggleSubject({
        variables: {
          courseId,
          subjectId: updatedSubject._id,
          status: updatedSubject.enabled ? 'enable' : 'disable',
        },
        optimisticResponse: {
          subject: {
            ...updatedSubject,
          },
        },
      });
    },
    [courseId, toggleSubject]
  );

  /**
   * Reorder Subject
   */
  const [reorderSubject] = useMutation(reorderSubjectMutation, {
    onError,
    update: updateSubjectsList,
  });
  const onReorderSubject = useCallback(
    ({ subjectId, parentSubjectId, from, to }) => {
      const updatedSubjects = locallyReorderSubjects(from, to, parentSubjectId);
      reorderSubject({
        variables: {
          courseId,
          subjectId,
          input: {
            parentSubjectId: parentSubjectId || undefined,
            from,
            to,
          },
        },
        optimisticResponse: {
          subjects: updatedSubjects,
        },
      });
    },
    [courseId, reorderSubject, locallyReorderSubjects]
  );

  return {
    subjects,
    loading,
    error,

    onCreateSubject,
    creatingSubject,

    onUpdateSubject,
    updatingSubject,

    onDeleteSubject,

    onToggleSubject,

    onReorderSubject,
  };
}
